import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { NeoCard, NeoButton, NeoInput } from '../NexusUI';
import { Lock, Loader2 } from 'lucide-react';

export function ForcePasswordChangeScreen() {
  const { session, user, signOut } = useAuth();
  
  // If user already updated password or doesn't need to change, redirect immediately
  useEffect(() => {
    if (user && user.mustChangePassword === false) {
      window.location.href = '/';
    }
  }, [user]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    
    if (password.length < 12) {
      setError("Password must be at least 12 characters long and meet strong password criteria");
      return;
    }
    
    setBusy(true);
    try {
      let token = session?.access_token;
      let userEmail = session?.user?.email;

      try {
        const { data: refreshData } = await supabase!.auth.refreshSession();
        if (refreshData?.session?.access_token) {
          token = refreshData.session.access_token;
          if (refreshData.session.user?.email) {
            userEmail = refreshData.session.user.email;
          }
        }
      } catch (err) {
        console.warn('Silent session refresh attempt:', err);
      }

      if (!token) {
        const sessionRes = await supabase!.auth.getSession();
        token = sessionRes?.data?.session?.access_token || session?.access_token;
        if (!userEmail) {
          userEmail = sessionRes?.data?.session?.user?.email;
        }
      }

      const response = await fetch('/api/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          email: userEmail,
          newPassword: password,
          isForced: true
        })
      });

      const resData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(resData.error || resData.message || 'Failed to update password');
      }
      
      if (resData.session?.access_token && resData.session?.refresh_token) {
        try {
          await supabase!.auth.setSession({
            access_token: resData.session.access_token,
            refresh_token: resData.session.refresh_token
          });
        } catch (setErr) {
          await supabase!.auth.refreshSession().catch(() => {});
        }
      } else {
        await supabase!.auth.refreshSession().catch(() => {});
      }

      window.dispatchEvent(new Event('compass_auth_changed'));
      window.location.href = '/';
      
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-theme p-4 transition-colors">
      <NeoCard className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
            <Lock className="h-6 w-6" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Change Password Required</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Welcome, {session?.user?.name || 'User'}. As this is your first time logging in or your password was reset, you must set a new password to continue.
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-500/10 p-3 text-sm font-medium text-red-500 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-1">New Password</label>
            <div className="relative">
              <NeoInput
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e: any) => setPassword(e.target.value)}
                placeholder="••••••••"
                icon={<Lock className="h-4 w-4" />}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-1">Confirm Password</label>
            <div className="relative">
              <NeoInput
                type={showPassword ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={(e: any) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                icon={<Lock className="h-4 w-4" />}
              />
            </div>
          </div>

          <div className="pt-2">
            <NeoButton
              type="submit"
              variant="primary"
              disabled={busy || !password || !confirmPassword}
              className="w-full flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Update Password
            </NeoButton>
          </div>
          
          <div className="mt-4 text-center">
            <button 
              type="button" 
              onClick={signOut}
              className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              Sign Out
            </button>
          </div>
        </form>
      </NeoCard>
    </div>
  );
}
